/**
 * <buy-buttons>: adds the selected variant to the cart without a page load.
 *
 * - Submits through window.MRLCart (assets/mrl-cart-bridge.js), which refreshes the cart drawer.
 *   Without it, the form submits normally.
 * - Follows the variant picker's `variant:change` event to update the button label and
 *   disabled state.
 */
(() => {
  if (customElements.get('buy-buttons')) return;

  class BuyButtons extends HTMLElement {
    #abort = new AbortController();

    connectedCallback() {
      const { signal } = this.#abort;

      this.addEventListener('submit', this.#handleSubmit, { signal });
      this.addEventListener('click', this.#handleQuantityClick, { signal });
      document.addEventListener('variant:change', this.#handleVariantChange, { signal });
    }

    disconnectedCallback() {
      this.#abort.abort();
    }

    get #form() {
      return this.querySelector('form');
    }

    get #submit() {
      return this.querySelector('button[type="submit"]');
    }

    /** @param {string} message - Empty to clear. */
    #showError(message) {
      const error = this.querySelector('[data-error]');
      if (!(error instanceof HTMLElement)) return;
      error.textContent = message;
      error.hidden = message === '';
    }

    /** @param {boolean} busy */
    #setBusy(busy) {
      const submit = this.#submit;
      const text = this.querySelector('[data-submit-text]');
      if (!(submit instanceof HTMLButtonElement) || !text) return;

      submit.setAttribute('aria-busy', String(busy));
      submit.disabled = busy;
      if (busy) text.textContent = this.dataset.addingText ?? '';
    }

    /** @param {boolean} available */
    #setAvailable(available) {
      const submit = this.#submit;
      const text = this.querySelector('[data-submit-text]');
      if (!(submit instanceof HTMLButtonElement) || !text) return;

      submit.disabled = !available;
      text.textContent = (available ? this.dataset.addText : this.dataset.soldOutText) ?? '';
    }

    /** @param {SubmitEvent} event */
    #handleSubmit = async (event) => {
      const cart = /** @type {any} */ (window).MRLCart;
      const form = this.#form;
      if (!cart || !form) return;

      event.preventDefault();
      if (this.#submit?.getAttribute('aria-busy') === 'true') return;

      const data = new FormData(form);
      const id = Number(data.get('id'));
      const quantity = Math.max(1, Number(data.get('quantity')) || 1);

      this.#showError('');
      this.#setBusy(true);

      try {
        await cart.addItem(id, quantity, { openDrawer: this.dataset.openDrawer === 'true' });
        this.dispatchEvent(new CustomEvent('buy-buttons:added', { bubbles: true, detail: { id, quantity } }));
      } catch (error) {
        this.#showError(error instanceof Error ? error.message : String(error));
      } finally {
        this.#setBusy(false);
        this.#setAvailable(!this.hasAttribute('data-variant-unavailable'));
      }
    };

    /** @param {MouseEvent} event */
    #handleQuantityClick = (event) => {
      if (!(event.target instanceof Element)) return;

      const button = event.target.closest('[data-quantity-minus], [data-quantity-plus]');
      const input = this.querySelector('input[name="quantity"]');
      if (!button || !(input instanceof HTMLInputElement)) return;

      const delta = button.hasAttribute('data-quantity-plus') ? 1 : -1;
      input.value = String(Math.max(1, (Number(input.value) || 1) + delta));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    };

    /** @param {Event} event */
    #handleVariantChange = (event) => {
      if (!(event instanceof CustomEvent) || !(event.target instanceof Element)) return;

      // Ignore pickers for other products on the page
      const picker = event.target.closest('variant-picker');
      if (picker instanceof HTMLElement && picker.dataset.productId !== this.dataset.productId) return;

      const { variant } = event.detail;
      const idInput = this.querySelector('input[name="id"]');
      if (idInput instanceof HTMLInputElement) idInput.value = String(variant.id);

      this.toggleAttribute('data-variant-unavailable', !variant.available);
      this.#showError('');
      this.#setAvailable(Boolean(variant.available));
    };
  }

  customElements.define('buy-buttons', BuyButtons);
})();
